// src/inventory.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import SavedToastOverlay from './components/SavedToastOverlay';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const emptyForm = {
    id: null,
    name: '',
    cost: '',
    markup: '',
    qtyOnHand: '',
    minStock: '',
    maxStock: '',
    reorderPoint: ''
  };

  const [formData, setFormData] = useState(emptyForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const calculateRetail = (override = null) => {
    const cost = Number((override?.cost ?? formData.cost) || 0);
    const markup = Number((override?.markup ?? formData.markup) || 0);
    return (cost + (cost * markup / 100)).toFixed(2);
  };

  const loadInventory = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/inventory`, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', ...getTenantHeaders() }
      });
      if (!res.ok) throw new Error(`Inventory load failed (${res.status})`);
      const data = await res.json();

      const mapped = Array.isArray(data)
        ? data.map((r) => ({
            id: r.id,
            name: r.partName || '',
            cost: r.cost ?? '',
            markup: r.markup ?? '',
            qtyOnHand: r.qtyOnHand ?? '',
            minStock: r.minStock ?? '',
            maxStock: r.maxStock ?? '',
            reorderPoint: r.reorderPoint ?? '',
            retail: calculateRetail({ cost: r.cost, markup: r.markup })
          }))
        : [];

      setItems(mapped);
    } catch (e) {
      console.error(e);
      setItems([]);
      alert('Error loading inventory from database.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const saveItem = async () => {
    if (!formData.name) return;
    const isUpdate = Boolean(formData.id);

    const payload = {
      partName: String(formData.name || '').trim(),
      cost: Number(formData.cost || 0),
      markup: Number(formData.markup || 0),
      qtyOnHand: formData.qtyOnHand === '' ? null : Number(formData.qtyOnHand),
      minStock: formData.minStock === '' ? null : Number(formData.minStock),
      maxStock: formData.maxStock === '' ? null : Number(formData.maxStock),
      reorderPoint: formData.reorderPoint === '' ? null : Number(formData.reorderPoint)
    };

    try {
      const res = await fetch(
        formData.id ? `${API_BASE}/api/inventory/${formData.id}` : `${API_BASE}/api/inventory`,
        {
          method: formData.id ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify(payload)
        }
      );
      if (!res.ok) throw new Error();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: isUpdate ? 'ITEM UPDATED!' : 'ITEM SAVED!' }
      }));
      setFormData(emptyForm);
      await loadInventory();
    } catch {
      alert('Error saving inventory item to database.');
    }
  };

  const editItem = (item) => {
    setFormData({ ...emptyForm, ...item });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${API_BASE}/api/inventory/${id}`, {
      method: 'DELETE',
      headers: { ...getTenantHeaders() }
    });
    loadInventory();
  };

  const styles = {
    container: { padding: isMobile ? '15px' : '40px' },

    title: {
      background: '#FFD400',
      border: '10px solid black',
      padding: '16px',
      marginBottom: '25px',
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '22px',
      position: 'relative'
    },

    // NEW: thin inner border inside title bar
    titleInner: {
      border: '2px solid rgba(0,0,0,0.55)',
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '10px'
    },

    // NEW: icon styling
    titleIcon: {
      fontSize: '22px',
      lineHeight: 1
    },

    commandCenter: {
      background: '#FFF3B0',
      border: '10px solid black',
      padding: isMobile ? '15px' : '30px',
      marginBottom: '40px'
    },

    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '6px',
      display: 'block'
    },

    input: {
      width: '100%',
      padding: '14px',
      border: '4px solid black',
      fontSize: '18px',
      fontWeight: 900,
      boxSizing: 'border-box'
    },

    searchWrap: {
      width: '100%',
      padding: '6px',
      marginBottom: '20px',
      background: '#754C38',
      boxSizing: 'border-box'
    },

    retailBox: {
      background: '#e0e0e0',
      border: '4px solid black',
      padding: '14px',
      textAlign: 'center',
      fontWeight: 900
    },

    submitBtn: {
      marginTop: '25px',
      width: '100%',
      padding: '25px',
      background: 'black',
      color: '#FFA500',
      fontSize: '24px',
      fontWeight: 900,
      border: '6px solid #E8761D',
      cursor: 'pointer',
      textTransform: 'uppercase'
    },

    listHeader: {
      background: '#FFD400',
      border: '8px solid black',
      padding: '12px',
      marginBottom: '12px',
      fontWeight: 900,
      textTransform: 'uppercase',
      position: 'relative'
    },

    // NEW: thin inner border inside list title bar
    listHeaderInner: {
      border: '2px solid rgba(0,0,0,0.55)',
      padding: '8px 10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '10px'
    },

    inventoryListArea: {
      border: '10px solid black',
      background: '#FFF3B0',
      padding: '12px',
      boxSizing: 'border-box'
    },

    inventoryList: {
      display: 'grid',
      gap: '14px',
      width: '100%',
    },

    inventoryCard: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '14px' : '24px',
      padding: isMobile ? '16px' : '18px 20px',
      border: '8px solid black',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      width: '100%',
      minWidth: 0
    },

    inventoryInfo: { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' },
    inventoryName: { fontSize: '18px', fontWeight: 900, marginBottom: '8px' },
    inventoryDetails: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px 22px',
      fontSize: '14px',
      fontWeight: 800
    },
    inventoryActions: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'flex-start' : 'flex-end',
      flex: '0 0 auto',
      maxWidth: '100%'
    },

    btnEdit: {
      background: '#FFD400',
      border: '4px solid black',
      fontWeight: 900,
      cursor: 'pointer',
      padding: '6px 12px'
    },

    btnDelete: {
      background: '#ff4444',
      border: '4px solid black',
      fontWeight: 900,
      cursor: 'pointer',
      padding: '6px 12px',
      color: 'white'
    }
  };

  const filtered = items.filter((i) => i.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const inventoryValue = items.reduce(
    (sum, item) => sum + (Number(item.qtyOnHand || 0) * Number(item.cost || 0)),
    0
  );
  const inventoryValueDisplay = inventoryValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <div className="jfn-inventory-page" style={styles.container}>
      <SavedToastOverlay accentColor="#FFD400" />
      <style>{`
        @media (max-width: 767px) {
          .jfn-inventory-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
        .jfn-inv-btn{
          transition: transform 120ms ease, filter 120ms ease, border-color 120ms ease, background 120ms ease, color 120ms ease;
        }
        .jfn-inv-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.03);
          background: #FFC107 !important;
          color: black !important;
          border-color: black !important;
        }
      `}</style>

      <TitleBar
        icon="📦"
        title="INVENTORY"
        accent="#754C38"
        right={
          <button
            className="jfn-inv-btn"
            style={{
              background: 'black',
              color: '#FFA500',
              border: '5px solid #E8761D',
              padding: '14px 16px',
              fontWeight: 900,
              cursor: 'pointer',
              textTransform: 'uppercase'
            }}
            type="button"
            onClick={() => (window.location.href = '/dashboard')}
          >
            ← BACK TO DASHBOARD
          </button>
        }
      />

      <div style={styles.commandCenter}>
        <label style={styles.label}>Item Name / Description</label>
        <input
          style={styles.input}
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="E.G. SPRAYER NOZZLE"
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6, 1fr)',
            gap: '15px',
            marginTop: '20px'
          }}
        >
          <div>
            <label style={styles.label}>Cost ($)</label>
            <input style={styles.input} name="cost" value={formData.cost} onChange={handleInputChange} />
          </div>

          <div>
            <label style={styles.label}>Markup (%)</label>
            <input style={styles.input} name="markup" value={formData.markup} onChange={handleInputChange} />
          </div>

          <div>
            <label style={styles.label}>Qty On Hand</label>
            <input style={styles.input} name="qtyOnHand" value={formData.qtyOnHand} onChange={handleInputChange} />
          </div>

          <div>
            <label style={styles.label}>Reorder Point</label>
            <input style={styles.input} name="reorderPoint" value={formData.reorderPoint} onChange={handleInputChange} />
          </div>

          <div>
            <label style={styles.label}>Min</label>
            <input style={styles.input} name="minStock" value={formData.minStock} onChange={handleInputChange} />
          </div>

          <div>
            <label style={styles.label}>Max</label>
            <input style={styles.input} name="maxStock" value={formData.maxStock} onChange={handleInputChange} />
          </div>
        </div>

        <div style={{ marginTop: '15px' }}>
          <label style={styles.label}>Retail (Auto)</label>
          <div style={styles.retailBox}>${calculateRetail()}</div>
        </div>

        <button className="jfn-inv-btn" style={styles.submitBtn} onClick={saveItem}>
          {formData.id ? 'Update Inventory Item' : 'Add Item'}
        </button>
      </div>

      <TitleBar
        icon="🔎"
        title="INVENTORY LIST"
        subtitle={`INVENTORY VALUE: ${inventoryValueDisplay}`}
        accent="#754C38"
      />

      <div style={styles.searchWrap}>
        <input
          style={{ ...styles.input, backgroundColor: 'white' }}
          placeholder="SEARCH INVENTORY..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={styles.inventoryListArea}>
        <div style={styles.inventoryList}>
          {loading ? (
            <div style={{ ...styles.inventoryCard, justifyContent: 'center', textAlign: 'center', fontWeight: 900 }}>
              Loading…
            </div>
          ) : (
            filtered.map((i) => {
              const hasQty = i.qtyOnHand !== '' && i.qtyOnHand != null;
              const hasReorderPoint = i.reorderPoint !== '' && i.reorderPoint != null;
              const hasMinStock = i.minStock !== '' && i.minStock != null;
              const hasMaxStock = i.maxStock !== '' && i.maxStock != null;

              return (
                <article key={i.id} style={styles.inventoryCard}>
                  <div style={styles.inventoryInfo}>
                    <div style={styles.inventoryName}>{i.name}</div>
                    <div style={styles.inventoryDetails}>
                      {hasQty && <span>QTY: {i.qtyOnHand}</span>}
                      <span style={{ color: '#054d3b' }}>RETAIL: ${i.retail}</span>
                      {hasReorderPoint && <span>REORDER: {i.reorderPoint}</span>}
                      {hasMinStock && hasMaxStock && <span>MIN / MAX: {i.minStock} / {i.maxStock}</span>}
                      {hasMinStock && !hasMaxStock && <span>MIN: {i.minStock}</span>}
                      {!hasMinStock && hasMaxStock && <span>MAX: {i.maxStock}</span>}
                    </div>
                  </div>

                  <div style={styles.inventoryActions}>
                    <button className="jfn-inv-btn" style={styles.btnEdit} onClick={() => editItem(i)}>
                      EDIT
                    </button>
                    <button className="jfn-inv-btn" style={styles.btnDelete} onClick={() => deleteItem(i.id)}>
                      DELETE
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Inventory;
